import { Link } from "react-router-dom";
import {
  BookOpen,
  CheckCircle2,
  ShieldCheck,
  Mail,
  MessageSquare,
  ExternalLink,
  Plug,
  KeyRound,
  Zap,
  FileText,
} from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";

const SUPPORT_EMAIL = "suporte@vyntric.com.br";

const STEPS = [
  {
    icon: Plug,
    title: "Acesse Integrações",
    body: (
      <>
        No painel do Vyntric, clique em <b>Integrações</b> no menu lateral. Você verá
        um card destinado ao <b>Bling ERP</b> com o botão{" "}
        <span className="inline-flex items-center gap-1 rounded-sm border border-emerald-300 bg-emerald-50 px-1.5 py-0.5 font-mono text-[11px] text-emerald-900">
          <Zap className="h-3 w-3" /> Conectar Bling
        </span>
        .
      </>
    ),
  },
  {
    icon: KeyRound,
    title: "Faça login no Bling",
    body: (
      <>
        Ao clicar em <b>Conectar Bling</b>, o Vyntric abre o portal oficial do{" "}
        <b>Bling ERP</b> em uma nova aba para você entrar com o e-mail e a senha
        da sua conta Bling. <b>Nenhuma senha nunca passa pelo Vyntric</b> — o
        login acontece 100% em <span className="font-mono">bling.com.br</span>.
      </>
    ),
  },
  {
    icon: ShieldCheck,
    title: "Autorize o acesso aos seus dados",
    body: (
      <>
        Após autenticar, o Bling exibirá a tela de <b>consentimento OAuth</b>{" "}
        listando os escopos solicitados pelo Vyntric:
        <ul className="mt-2 list-inside list-disc space-y-1 text-sm">
          <li>Pedidos de venda (leitura e escrita)</li>
          <li>Notas fiscais eletrônicas (leitura, emissão e envio ao SEFAZ)</li>
          <li>Produtos e situações (leitura)</li>
        </ul>
        Clique em <b>Autorizar</b>. Você é redirecionado de volta ao Vyntric e o
        card de integração passa a exibir <b>Conectado</b>.
      </>
    ),
  },
  {
    icon: Zap,
    title: "O Vyntric começa a operar automaticamente",
    body: (
      <>
        A partir daí, o sistema sincroniza seus pedidos aprovados de marketplaces
        no ciclo de <b>5 minutos</b>, aplica as regras de desconto configuradas e
        emite a NF-e via Bling — tudo <b>sem que você precise voltar aqui</b>. Os
        tokens de acesso são renovados automaticamente em segundo plano.
      </>
    ),
  },
];

const FAQ = [
  {
    q: "Preciso instalar algum aplicativo ou plug-in?",
    a: "Não. A conexão Bling ↔ Vyntric é totalmente OAuth 2.0 — feita pelo navegador em segundos. Nada é instalado no seu computador ou no seu Bling.",
  },
  {
    q: "Posso desconectar quando quiser?",
    a: "Sim. Em Integrações → Bling ERP → Desconectar, o Vyntric revoga a autorização localmente. Você também pode revogar direto no portal do Bling em Minha Conta → Aplicações.",
  },
  {
    q: "O que acontece se o token expirar?",
    a: "O Vyntric renova o token automaticamente antes de qualquer expiração (job a cada 30 min). Se o refresh_token do Bling for revogado (raro), o painel exibe um aviso pedindo para reautorizar.",
  },
  {
    q: "Quais dados o Vyntric lê do meu Bling?",
    a: "Somente o necessário para o ciclo fiscal: pedidos aprovados, produtos vinculados e situações de NF-e. Não temos acesso aos seus dados bancários, financeiro ou cadastros pessoais.",
  },
];

export default function BlingInstructions() {
  return (
    <div className="min-h-screen bg-slate-50" data-testid="bling-instructions-page">
      {/* Public header — no dependency on the authenticated Layout */}
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center gap-2 text-slate-900">
            <div className="flex h-8 w-8 items-center justify-center rounded bg-slate-900 text-white">
              <BookOpen className="h-4 w-4" />
            </div>
            <div>
              <div className="text-sm font-black tracking-tight">Vyntric</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">
                Documentação pública
              </div>
            </div>
          </Link>
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            className="hidden items-center gap-1.5 text-xs font-semibold text-slate-600 hover:text-slate-900 sm:inline-flex"
          >
            <Mail className="h-3.5 w-3.5" />
            {SUPPORT_EMAIL}
          </a>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-6 py-10">
        <div className="mb-8">
          <Badge variant="outline" className="mb-3 font-mono text-[10px] uppercase tracking-widest">
            Manual de integração · v1
          </Badge>
          <h1 className="text-4xl font-black tracking-tighter text-slate-900 sm:text-5xl">
            Guia de Integração Bling &amp; Vyntric
          </h1>
          <p className="mt-4 max-w-2xl text-base text-slate-600">
            Este documento descreve, passo a passo, como conectar sua conta{" "}
            <b>Bling ERP</b> ao <b>Vyntric</b> para automatizar a emissão de
            NF-e, sincronizar pedidos de marketplaces e centralizar sua
            expedição. A conexão é feita por OAuth 2.0 — segura, revogável e
            sem digitar senha no Vyntric.
          </p>
        </div>

        {/* Steps */}
        <div className="space-y-4">
          {STEPS.map((step, idx) => {
            const Icon = step.icon;
            return (
              <Card
                key={idx}
                className="border-slate-200"
                data-testid={`bling-instr-step-${idx + 1}`}
              >
                <CardHeader className="flex flex-row items-start gap-4 pb-3">
                  <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md bg-slate-900 text-white">
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="flex-1 space-y-1">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400">
                      Passo {idx + 1} de {STEPS.length}
                    </div>
                    <CardTitle className="text-xl tracking-tight">
                      {step.title}
                    </CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="pl-20 text-sm leading-relaxed text-slate-700">
                  {step.body}
                </CardContent>
              </Card>
            );
          })}
        </div>

        <Separator className="my-10" />

        {/* Security note */}
        <Card
          className="border-emerald-200 bg-emerald-50/60"
          data-testid="bling-instr-security"
        >
          <CardHeader className="flex flex-row items-start gap-3 pb-2">
            <ShieldCheck className="mt-0.5 h-5 w-5 flex-shrink-0 text-emerald-700" />
            <div>
              <CardTitle className="text-base tracking-tight text-emerald-900">
                Segurança &amp; conformidade
              </CardTitle>
              <CardDescription className="text-emerald-800/80">
                Todas as credenciais OAuth ficam <b>criptografadas em repouso</b>{" "}
                (Fernet AES-128 + HMAC).
              </CardDescription>
            </div>
          </CardHeader>
          <CardContent className="pl-11 text-sm text-emerald-900">
            <ul className="space-y-1.5">
              <li className="flex items-start gap-2">
                <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-700" />
                <span>Fluxo 100% OAuth 2.0 — nunca armazenamos sua senha do Bling.</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-700" />
                <span>Tokens rotacionados automaticamente (job a cada 30 min).</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-700" />
                <span>Rate limit interno ≤ 2,5 req/s no Bling — jamais ultrapassa sua cota.</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-700" />
                <span>Desconexão pelo cliente a qualquer momento — sem burocracia.</span>
              </li>
            </ul>
          </CardContent>
        </Card>

        {/* FAQ */}
        <section className="mt-10" data-testid="bling-instr-faq">
          <div className="mb-4 flex items-center gap-2">
            <FileText className="h-4 w-4 text-slate-500" />
            <h2 className="text-lg font-black tracking-tight text-slate-900">
              Perguntas frequentes
            </h2>
          </div>
          <div className="space-y-3">
            {FAQ.map((it, i) => (
              <Card key={i} className="border-slate-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-bold text-slate-900">
                    {it.q}
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-sm text-slate-700">
                  {it.a}
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Support */}
        <Card
          className="mt-10 border-slate-900 bg-slate-900 text-white"
          data-testid="bling-instr-support"
        >
          <CardHeader className="flex flex-row items-start gap-3">
            <MessageSquare className="mt-0.5 h-5 w-5 flex-shrink-0 text-white" />
            <div>
              <CardTitle className="text-lg tracking-tight text-white">
                Precisa de ajuda?
              </CardTitle>
              <CardDescription className="text-slate-300">
                Nossa equipe responde em até 24 horas úteis.
              </CardDescription>
            </div>
          </CardHeader>
          <CardContent className="space-y-3 pl-11 text-sm">
            <div className="flex items-center gap-2 text-slate-200">
              <Mail className="h-4 w-4 text-slate-400" />
              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                className="font-mono text-white underline-offset-4 hover:underline"
                data-testid="bling-instr-support-email"
              >
                {SUPPORT_EMAIL}
              </a>
            </div>
            <div className="flex items-center gap-2 text-slate-200">
              <ExternalLink className="h-4 w-4 text-slate-400" />
              <a
                href="https://vyntric.com.br"
                className="text-white underline-offset-4 hover:underline"
                target="_blank"
                rel="noreferrer"
              >
                vyntric.com.br
              </a>
            </div>
            <p className="pt-2 text-xs text-slate-400">
              Ao abrir um chamado, informe seu <b>e-mail cadastrado no Vyntric</b>{" "}
              e o <b>número do pedido / NF-e</b> quando aplicável — assim
              conseguimos investigar mais rápido.
            </p>
          </CardContent>
        </Card>

        <footer className="mt-10 pb-6 text-center text-[10px] uppercase tracking-widest text-slate-400">
          © Vyntric · Documento público · Última revisão em fevereiro/2026
        </footer>
      </main>
    </div>
  );
}
